import { Button, Grid, Typography } from '@material-ui/core';
import { Dispatch, SetStateAction, VFC, useContext } from 'react';
import { useHooks } from './useHooks';
import { useStyles } from './useStyles';
import { BaseModal } from '../../../molecules/BaseModal';
import { parsePrice } from '../../../../utils';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import { formatDatetime } from '../../../../utils/common';
import { TDailyReportPageForm } from '../../../../types/dailyReport';
import { DailyReportImageBase64ListItem } from '../DailyReportImageBase64ListItem';
import LoadingIconClear from '../../../atoms/LoadingIconClear';
import { AuthInfoContext } from '../../../../AuthContextProvider';
import { ROLE } from '../../../../const/Const';
import { CustomTooltip } from '../../../tooltip/CustomTooltip';
import { tooltipContents } from '../../../tooltip/Const';
import HelpIcon from '@material-ui/icons/Help';

export type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
  dailyReportId: number;
  setDailyReportId: Dispatch<number>;
  searchParams: TDailyReportPageForm;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  renderDailyReportTrigger: boolean;
};

export const DailyReportDetailModal: VFC<Props> = props => {
  const {
    isOpen,
    setIsOpen,
    dailyReportId,
    setDailyReportId,
    searchParams,
    currentPage,
    setCurrentPage,
    renderDailyReportTrigger
  } = props;

  const classes = useStyles();
  const [authInfo, setAuthInfo] = useContext(AuthInfoContext);

  const {
    moveNext,
    movePrev,
    dailyReportDetail,
    disabledMovePrev,
    disabledMoveNext,
    isSupervisor,
    isWorker,
    isLoading
  } = useHooks({
    isOpen,
    dailyReportId,
    setDailyReportId,
    searchParams,
    currentPage,
    setCurrentPage,
    renderDailyReportTrigger
  });

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleSubmit={() => {}}
      handleCancelSubmit={() => {}}
      isCloseIcon={true}
      isSubmitButton={false}
      isCancelButton={false}
    >
      {isLoading && (
        <div className={classes.loadingIconClearWrapper}>
          <LoadingIconClear />
        </div>
      )}
      {/* 日付 */}
      <Grid
        container
        alignItems='center'
        justify='center'
        className={classes.detailHeader}
      >
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.dateLabel}>
            {dailyReportDetail && (
              <>
                {formatDatetime(
                  new Date(dailyReportDetail?.work_day),
                  'YYYY年MM月DD日'
                )}
              </>
            )}
          </label>
        </Grid>
        <Grid item xs={10} md={7} className={`${classes.gridItem}`}>
          <Button onClick={movePrev} disabled={disabledMovePrev}>
            <ArrowLeftIcon className={classes.moveIcon} />
            前へ
          </Button>
          <Button onClick={moveNext} disabled={disabledMoveNext}>
            次へ
            <ArrowRightIcon className={classes.moveIcon} />
          </Button>
        </Grid>
      </Grid>
      {/* /日付 */}
      <Grid
        container
        xs={10}
        alignItems='center'
        justify='center'
        className={isLoading ? classes.visibilityHidden : ''}
      >
        {/* 案件 */}
        <Grid container alignItems='center' justify='center'>
          <Grid item xs={10} md={2} className={classes.gridItem}>
            <label className={classes.label}>案件</label>
          </Grid>
          <Grid
            item
            xs={10}
            md={10}
            className={`${classes.gridItem} ${classes.textItemWrapper}`}
          >
            <Typography variant='body2'>
              {dailyReportDetail?.constructs.name}
            </Typography>
          </Grid>
        </Grid>
        {/* /案件 */}
        {isWorker() && (
          <>
            {/* 社名 */}
            <Grid container alignItems='center' justify='center'>
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>社名</label>
              </Grid>
              <Grid
                item
                xs={10}
                md={10}
                className={`${classes.gridItem} ${classes.textItemWrapper}`}
              >
                <Typography variant='body2'>
                  {dailyReportDetail?.company_name}
                </Typography>
              </Grid>
            </Grid>
            {/* /社名 */}
            {/* 記入者 */}
            <Grid container alignItems='center' justify='center'>
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>記入者</label>
              </Grid>
              <Grid
                item
                xs={10}
                md={10}
                className={`${classes.gridItem} ${classes.textItemWrapper}`}
              >
                <Typography variant='body2'>
                  {dailyReportDetail?.writer_name}
                </Typography>
              </Grid>
            </Grid>
            {/* /記入者 */}
          </>
        )}
        {isWorker() && (
          <>
            {/* 作業内容 */}
            <Grid container alignItems='center' justify='center'>
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>作業内容</label>
              </Grid>
              <Grid item xs={10} md={10} className={classes.gridItem}>
                <Typography variant='body2'>
                  {dailyReportDetail?.site_process?.name}
                </Typography>
              </Grid>
            </Grid>
            {/* /作業内容 */}
          </>
        )}
        {/* 報告 */}
        <Grid item xs={10} md={2} className={classes.gridItem}>
          <label className={classes.label}>報告</label>
        </Grid>
        <Grid item xs={10} md={10} className={classes.textItemWrapper}>
          <Typography variant='body2'>
            {dailyReportDetail?.report_detail.split('\n').map(str => {
              return (
                <>
                  {str}
                  <br />
                </>
              );
            })}
          </Typography>
        </Grid>
        {/* /報告 */}
        {/* 写真 */}
        <Grid item xs={10} md={2} className={classes.gridItem}>
          <label className={classes.label}>写真</label>
        </Grid>
        <Grid item xs={10} md={10} className={classes.gridItem}>
          {dailyReportDetail?.daily_report_images &&
            dailyReportDetail?.daily_report_images.length > 0 && (
              <>
                <div className={classes.imageListContainer}>
                  {dailyReportDetail?.daily_report_images.map(
                    (imageInfo: { id: number; image: string }) => (
                      <DailyReportImageBase64ListItem
                        base64String={imageInfo.image}
                        index={imageInfo.id}
                        listName={'imageList'}
                      />
                    )
                  )}
                </div>
              </>
            )}
        </Grid>
        {/* /写真 */}
        {isSupervisor() && authInfo?.role != ROLE['OUTSIDE_PARTNER'] && (
          <>
            {/* 原価UP */}
            <Grid container alignItems='flex-start' justify='center'>
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>
                  原価UP
                  <br />
                  要因
                </label>
              </Grid>
              <Grid
                item
                xs={10}
                md={6}
                className={`${classes.gridItem} ${classes.textItemWrapper}`}
              >
                <Typography variant='body2'>
                  {dailyReportDetail?.cost_up?.factor &&
                    dailyReportDetail?.cost_up?.factor.split('\n').map(str => {
                      return (
                        <>
                          {str}
                          <br />
                        </>
                      );
                    })}
                </Typography>
              </Grid>
              <Grid item xs={10} md={4} className={classes.costSubContainer}>
                <label className={`${classes.label} ${classes.gridItem}`}>
                  目安金額
                </label>
                <Typography variant='body2' className={classes.gridItem}>
                  {parsePrice(dailyReportDetail?.cost_up?.amount)}
                </Typography>
              </Grid>
            </Grid>
            {/* /原価UP */}
            {/* 原価DOWN */}
            <Grid container alignItems='flex-start' justify='center'>
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>
                  原価DOWN
                  <br />
                  要因
                </label>
              </Grid>
              <Grid
                item
                xs={10}
                md={6}
                className={`${classes.gridItem} ${classes.textItemWrapper}`}
              >
                <Typography variant='body2'>
                  {dailyReportDetail?.cost_down?.factor &&
                    dailyReportDetail?.cost_down?.factor
                      .split('\n')
                      .map(str => {
                        return (
                          <>
                            {str}
                            <br />
                          </>
                        );
                      })}
                </Typography>
              </Grid>
              <Grid item xs={10} md={4} className={classes.costSubContainer}>
                <label className={`${classes.label} ${classes.gridItem}`}>
                  目安金額
                </label>
                <Typography variant='body2' className={classes.gridItem}>
                  {parsePrice(dailyReportDetail?.cost_down?.amount)}
                </Typography>
              </Grid>
            </Grid>
            {/* /原価DOWN */}
          </>
        )}
        {authInfo?.role !== ROLE.OUTSIDE_PARTNER && (
          <Grid container justifyContent='flex-end'>
            <CustomTooltip
              title={tooltipContents.DailyReportOutsidePartner}
              placement='left'
            >
              <HelpIcon color='action' />
            </CustomTooltip>
          </Grid>
        )}
      </Grid>
    </BaseModal>
  );
};
